import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../context/AppContext";
import { useNespresso } from "../context/NespressoContext";
import { getTierById } from "../../../config/tiers";

// Nespresso brand colors
const NESPRESSO = {
  brown: "#986f38",
  brownHover: "#815e2f",
  gold: "#c69f36",
  black: "#000000",
  darkGray: "#292929",
  lightGray: "#f9f9f9",
  border: "#e6e5e4",
};

const ConfirmationView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { state: nespressoState, dispatch: nespressoDispatch, currentTier } = useNespresso();
  const [sessionId] = useState(() => `nespresso-${Date.now()}`);
  const [showCelebration, setShowCelebration] = useState(false);

  const lifetimePods = nespressoState.gamification.lifetimePods;
  const celebratingTierId = nespressoState.gamification.celebratingTierId;

  // Calculate total credit from empties
  const totalCredit = state.empties.reduce((sum, item) => {
    const dollarValue = item.product.rewards?.dollarValue;
    if (dollarValue) {
      return sum + parseFloat(dollarValue.replace("$", ""));
    }
    return sum;
  }, 0);

  // Get products list from empties for display
  const products = state.empties.map((item) => item.product);

  // Calculate total capsules - use empties count as a proxy
  const totalCapsules = state.empties.length;

  // Check for celebration on mount
  useEffect(() => {
    if (celebratingTierId) {
      setShowCelebration(true);
    }
  }, [celebratingTierId]);

  // Send completion event on mount
  useEffect(() => {
    const sendCompletionEvent = async () => {
      try {
        await fetch(
          "https://agentic-commerce.pentatonic.com/api/v1/pin/events",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Tenant-ID": "nespresso",
            },
            body: JSON.stringify({
              sessionId,
              eventType: "recycling_completed",
              eventData: {
                creditValue: totalCredit,
                capsuleCount: totalCapsules,
                productNames: products.map((p) => p.name),
                store: "Nespresso Boutique",
                lifetimePods,
                currentTier: currentTier.name,
              },
              timestamp: new Date().toISOString(),
            }),
          }
        );
      } catch (error) {
        console.log("Event tracking skipped (demo mode)");
      }
    };

    sendCompletionEvent();
    localStorage.setItem("nespresso_last_session", sessionId);
  }, [sessionId, totalCredit, totalCapsules, products, lifetimePods, currentTier.name]);

  const handleDismissCelebration = () => {
    nespressoDispatch({ type: "DISMISS_CELEBRATION" });
    setShowCelebration(false);
  };

  const handleDone = () => {
    dispatch({ type: "RESET" });
    navigate("/nespresso");
  };

  const handleNewRecycling = () => {
    dispatch({ type: "RESET" });
    navigate("/nespresso/takeback");
  };

  const steps = [
    {
      title: "Sort & Separate",
      description: "Aluminum separated from coffee grounds",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
    },
    {
      title: "Recycle Aluminum",
      description: "Capsules melted for infinite reuse",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      ),
    },
    {
      title: "Compost Grounds",
      description: "Coffee becomes nutrient-rich soil",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  const celebrationTier = celebratingTierId ? getTierById(celebratingTierId) : null;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: NESPRESSO.black }}
    >
      {/* Tier Advancement Celebration Modal */}
      <AnimatePresence>
        {showCelebration && celebrationTier && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
            onClick={handleDismissCelebration}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-lg p-8 max-w-md mx-4 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Celebration Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: NESPRESSO.gold }}
              >
                <svg
                  className="w-10 h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
              </motion.div>

              <h2
                className="text-2xl font-medium mb-2"
                style={{ color: NESPRESSO.black }}
              >
                Congratulations!
              </h2>
              <p className="text-gray-600 mb-4">You've reached a new tier</p>

              <div
                className="inline-block px-6 py-3 rounded-lg mb-6"
                style={{ backgroundColor: `${NESPRESSO.gold}20` }}
              >
                <p
                  className="text-xl font-medium"
                  style={{ color: NESPRESSO.brown }}
                >
                  {celebrationTier.name}
                </p>
              </div>

              {celebrationTier.reward && (
                <div
                  className="p-4 rounded-lg mb-6"
                  style={{ backgroundColor: NESPRESSO.lightGray }}
                >
                  <p className="text-sm text-gray-500 mb-1">Unlocked Reward</p>
                  <p
                    className="font-medium"
                    style={{ color: NESPRESSO.black }}
                  >
                    {celebrationTier.reward.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {celebrationTier.reward.description}
                  </p>
                </div>
              )}

              <button
                onClick={handleDismissCelebration}
                className="px-8 py-3 text-white font-medium transition-colors"
                style={{
                  backgroundColor: NESPRESSO.brown,
                  borderRadius: "3px",
                }}
              >
                Continue
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="py-6 px-8 flex items-center justify-center">
        <img
          src="/assets/images/logos/nespresso-white.png"
          alt="Nespresso"
          className="h-6 w-auto"
        />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 py-8">
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6 }}
          className="w-24 h-24 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: NESPRESSO.gold }}
        >
          <svg
            className="w-12 h-12 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-light text-white mb-3">
            Thank You for Recycling!
          </h1>
          <p className="text-lg text-white/60 font-light">
            Your contribution helps create a more sustainable future
          </p>
        </motion.div>

        {/* Receipt Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg p-8 max-w-md w-full mb-8 shadow-2xl"
        >
          <div
            className="flex items-center justify-between mb-6 pb-6"
            style={{ borderBottom: `1px solid ${NESPRESSO.border}` }}
          >
            <div>
              <p className="text-gray-500 text-sm">Capsules Recycled</p>
              <p
                className="text-3xl font-medium"
                style={{ color: NESPRESSO.black }}
              >
                {totalCapsules}
              </p>
            </div>
            <div className="text-right">
              <p className="text-gray-500 text-sm">Credit Earned</p>
              <p
                className="text-3xl font-medium"
                style={{ color: NESPRESSO.brown }}
              >
                ${totalCredit.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Items list */}
          <div className="space-y-3 mb-6">
            {products.map((product, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-gray-600 font-light">{product.name}</span>
                <span
                  className="font-medium"
                  style={{ color: NESPRESSO.black }}
                >
                  {product.rewards?.dollarValue || "$0.00"}
                </span>
              </div>
            ))}
          </div>

          {/* Environmental Impact */}
          <div
            className="py-4 mb-4 rounded-lg text-center"
            style={{ backgroundColor: NESPRESSO.lightGray }}
          >
            <p className="text-sm text-gray-500 mb-1">Environmental Impact</p>
            <p className="font-medium" style={{ color: NESPRESSO.brown }}>
              {(totalCapsules * 0.8).toFixed(0)}g aluminum saved
            </p>
          </div>

          {/* Lifetime Progress */}
          <div
            className="py-4 mb-4 rounded-lg text-center"
            style={{ backgroundColor: `${NESPRESSO.gold}10` }}
          >
            <p className="text-sm text-gray-500 mb-1">Lifetime Progress</p>
            <p className="font-medium" style={{ color: NESPRESSO.brown }}>
              {lifetimePods.toLocaleString()} pods • {currentTier.name}
            </p>
          </div>

          {/* Session ID */}
          <div
            className="text-center pt-4"
            style={{ borderTop: `1px solid ${NESPRESSO.border}` }}
          >
            <p className="text-gray-400 text-xs">Confirmation #{sessionId}</p>
          </div>
        </motion.div>

        {/* What Happens Next */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="max-w-2xl w-full mb-8"
        >
          <h3 className="text-white font-medium text-center mb-6">
            What Happens to Your Capsules
          </h3>
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.title}>
                <div className="flex flex-col items-center text-center">
                  <div
                    className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mb-3"
                    style={{ color: NESPRESSO.gold }}
                  >
                    {step.icon}
                  </div>
                  <h4 className="text-white font-medium text-sm mb-1">
                    {step.title}
                  </h4>
                  <p className="text-white/50 text-xs max-w-[120px] font-light">
                    {step.description}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 h-px bg-white/20 mx-4 mt-[-24px]" />
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex gap-4"
        >
          <button
            onClick={handleNewRecycling}
            className="px-8 py-3 bg-white/10 text-white font-medium hover:bg-white/20 transition-colors"
            style={{ borderRadius: "3px" }}
          >
            Recycle More
          </button>
          <button
            onClick={handleDone}
            className="px-8 py-3 bg-white font-medium hover:bg-gray-100 transition-colors"
            style={{
              color: NESPRESSO.black,
              borderRadius: "3px",
            }}
          >
            Done
          </button>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-4 px-8 text-center text-white/40 text-sm border-t border-white/10 font-light">
        <p>Your credit has been added to your Nespresso Club account</p>
      </footer>
    </div>
  );
};

export default ConfirmationView;
